import { Input } from "antd"
import type { InputProps } from "antd/es/input"
import React from "react"

type CountryCode = "US"

interface CountryConfig {
  code: string
  phoneMask: (digits: string) => string
  maxDigits: number
  placeholder: string
}

const countryConfigs: Record<CountryCode, CountryConfig> = {
  US: {
    code: "1",
    maxDigits: 11,
    placeholder: "+1 (XXX) XXX-XXXX",
    phoneMask: (digits) => {
      if (digits.length === 0) return ""
      if (digits.length <= 1) return `+${digits}`
      if (digits.length <= 4) return `+${digits[0]} (${digits.slice(1)}`
      if (digits.length <= 7)
        return `+${digits[0]} (${digits.slice(1, 4)}) ${digits.slice(4)}`
      return `+${digits[0]} (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
    }
  }
}

interface PhoneInputProps extends Omit<InputProps, "onChange" | "value"> {
  value?: string | null
  country?: CountryCode
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
}

export const PhoneInput = ({
  value,
  onChange,
  country = "US",
  placeholder,
  ...rest
}: PhoneInputProps) => {
  const config = countryConfigs[country]

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    let digits = raw.replace(/\D/g, "").slice(0, config.maxDigits)

    // Country code auto-substitution
    if (digits.length > 0 && !digits.startsWith(config.code)) {
      // If the user starts typing without a code, add it
      if (digits.length <= config.maxDigits - config.code.length) {
        digits = config.code + digits
      }
    }

    digits = digits.slice(0, config.maxDigits)

    const itu = digits.length > 0 ? `+${digits}` : ""

    onChange?.({
      ...e,
      target: { ...e.target, value: itu },
      currentTarget: { ...e.currentTarget, value: itu }
    } as React.ChangeEvent<HTMLInputElement>)
  }

  const digits = value?.replace(/\D/g, "") ?? ""
  const formattedValue = config.phoneMask(digits)

  return (
    <Input
      value={formattedValue}
      onChange={handleChange}
      placeholder={placeholder ?? config.placeholder}
      {...rest}
    />
  )
}
